import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { authHeaders, Toast } from './listPageStyles';
import { unwrapLookupItems } from './c360';
import { usePageMeta } from '../hooks/usePageMeta';
import { useDistributorScope } from '../hooks/useDistributorScope';
import {
  DetailShell, Section, Grid, Icon, SubModal, SplitButton,
  FieldText, FieldSelect,
} from './detail/DetailShell';
import { usePageActions } from '../contexts/PermissionContext';

const API_BASE = '/api/productdynamicgroups';

const TYPE_GROUP = 1;
const TYPE_SUBGROUP = 2;
const TYPE_CODE = 3;
const TYPE_OPTIONS = [
  { value: TYPE_GROUP, label: 'Grup' },
  { value: TYPE_SUBGROUP, label: 'Ek Grup' },
  { value: TYPE_CODE, label: 'Kod' },
];

// #725 İki panelli havuz kurucu — inline stiller (paylaşılan CSS'e dokunmadan, tema token'lı fallback'ler).
const PS = {
  wrap: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' },
  col: { display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 },
  tabs: { display: 'flex', gap: 6 },
  tab: (active) => ({
    flex: 1, padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
    border: `1px solid ${active ? 'var(--brand-blue-1, #2563eb)' : 'var(--border-color, #d1d5db)'}`,
    background: active ? 'var(--brand-blue-1, #2563eb)' : 'var(--surface, #fff)',
    color: active ? '#fff' : 'var(--text-secondary, #374151)',
  }),
  search: {
    padding: '8px 10px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', fontSize: 13,
    background: 'var(--surface, #fff)', color: 'var(--text-primary, #111827)',
  },
  list: {
    border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 8, maxHeight: 320, overflowY: 'auto',
    background: 'var(--surface, #fff)',
  },
  hint: { padding: '18px 12px', textAlign: 'center', fontSize: 13, color: 'var(--text-secondary, #6b7280)' },
  item: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
    borderBottom: '1px solid var(--border-color, #f1f5f9)', cursor: 'pointer', fontSize: 13,
  },
  code: { fontWeight: 700, fontFamily: 'ui-monospace, monospace', color: 'var(--text-primary, #111827)', flexShrink: 0 },
  desc: { color: 'var(--text-secondary, #6b7280)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  add1: {
    border: 'none', background: 'transparent', color: 'var(--brand-blue-1, #2563eb)',
    fontSize: 18, lineHeight: 1, cursor: 'pointer', padding: '0 4px', flexShrink: 0,
  },
  transfer: { marginTop: 2 },
  target: (drag) => ({
    border: `2px dashed ${drag ? 'var(--brand-blue-1, #2563eb)' : 'var(--border-color, #d1d5db)'}`,
    borderRadius: 10, minHeight: 320, padding: 12, background: drag ? 'rgba(37,99,235,0.06)' : 'var(--surface-alt, #f8fafc)',
    transition: 'border-color .15s, background .15s',
  }),
  poolHead: { fontSize: 13, fontWeight: 700, color: 'var(--text-secondary, #374151)', marginBottom: 10 },
  empty: { padding: '40px 16px', textAlign: 'center', fontSize: 13, color: 'var(--text-secondary, #6b7280)', lineHeight: 1.6 },
  chips: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 },
  chip: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 10px',
    borderRadius: 8, background: 'var(--surface, #fff)', border: '1px solid var(--border-color, #e5e7eb)', fontSize: 13,
  },
  chipX: { border: 'none', background: 'transparent', color: 'var(--danger, #dc2626)', cursor: 'pointer', fontSize: 15, lineHeight: 1, flexShrink: 0 },
};

// #828 vendorId — zorunlu değil; boş = tüm distribütörler için geçerli özel grup tanımı.
const EMPTY_FORM = { id: '', textId: '', description: '', vendorId: '' };

export function ProductDynamicGroupDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isNew = !id || id === 'new';
  const { canAdd, canEdit, canDelete } = usePageActions();
  const canSave = isNew ? canAdd : canEdit;
  usePageMeta(isNew ? 'Yeni Özelleştirilmiş Grup' : 'Özelleştirilmiş Grup Detayı', 'Grup, ek grup veya kod bazında dinamik ürün havuzları tanımlayın.');

  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [toast, setToast] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [audit, setAudit] = useState(null);

  // #828 Distribütör kapsamı — seçenekler kullanıcının yetkili olduğu distribütörler.
  const { vendorOptions, lockedOptions, isDistributorUser, lockedVendorId, scopeLoaded } = useDistributorScope();

  // Child-grid: ürün havuzu kriterleri
  const [criteria, setCriteria] = useState([]); // { id, criteriaType, groupId, subGroupId, productId, label }
  const [deletedDetailIds, setDeletedDetailIds] = useState([]);

  // #725 İki panelli havuz kurucu — sol panel durumu
  const [sourceType, setSourceType] = useState(TYPE_GROUP);
  const [sourceSearch, setSourceSearch] = useState('');
  const [checkedSource, setCheckedSource] = useState(() => new Set());
  const [productResults, setProductResults] = useState([]);
  const [productSearching, setProductSearching] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const dragItemRef = useRef(null);
  // #742 Havuz (sağ panel) için filtre + çoklu seçim.
  const [poolFilter, setPoolFilter] = useState('');
  const [poolChecked, setPoolChecked] = useState(() => new Set());

  // Havuz önizleme (Section)
  const [previewRows, setPreviewRows] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Lookup'lar
  const [groups, setGroups] = useState([]);
  const [subGroups, setSubGroups] = useState([]);

  const showToast = (type, message) => { setToast({ type, message }); setTimeout(() => setToast(null), 3500); };
  const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

  // Grup / ek grup lookup'larını yükle
  useEffect(() => {
    const headers = authHeaders();
    Promise.all([
      fetch('/api/productgroups/Select', { headers }).then((res) => (res.ok ? res.json() : [])),
      fetch('/api/productsubgroups/Select', { headers }).then((res) => (res.ok ? res.json() : [])),
    ]).then(([groupData, subGroupData]) => {
      setGroups(unwrapLookupItems(groupData));
      setSubGroups(unwrapLookupItems(subGroupData));
    }).catch(() => {});
  }, []);

  // Tek kayıt yükleme: önce location.state.row ile başlık, sonra GetById ile detay + kriter etiketleri.
  const load = useCallback(async () => {
    if (isNew) { setLoading(false); return; }
    setLoading(true);
    const row = location.state?.row;
    if (row) {
      setForm({ id: row.id, textId: row.textId || '', description: row.description || '', vendorId: row.vendorId ?? row.VendorId ?? '' });
    }
    try {
      const res = await fetch(`${API_BASE}/GetById?Id=${id}`, { headers: authHeaders() });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Detay alınamadı');
      const payload = body?.data ?? body?.Data ?? {};
      const header = payload.header || payload.Header || {};
      setForm({
        id: header.id ?? header.Id ?? id,
        textId: header.textId ?? header.TextId ?? '',
        description: header.description ?? header.Description ?? '',
        vendorId: header.vendorId ?? header.VendorId ?? '',
      });
      setAudit({ createdBy: header.createdBy ?? header.CreatedBy ?? header.createdUser ?? header.CreatedUser, createdDate: header.createdDate ?? header.CreatedDate, updatedBy: header.updatedBy ?? header.UpdatedBy ?? header.modifiedUser ?? header.ModifiedUser, updatedDate: header.updatedDate ?? header.UpdatedDate ?? header.modifiedDate ?? header.ModifiedDate });
      const detailList = (payload.details || payload.Details || []).map((d) => ({
        id: d.id ?? d.Id,
        criteriaType: d.criteriaType ?? d.CriteriaType,
        groupId: d.groupId ?? d.GroupId ?? null,
        subGroupId: d.subGroupId ?? d.SubGroupId ?? null,
        productId: d.productId ?? d.ProductId ?? null,
        label: d.label ?? d.Label ?? '',
      }));
      setCriteria(detailList);
      setDeletedDetailIds([]);
      setPreviewRows(null);
      setPoolChecked(new Set());
      setPoolFilter('');
    } catch (error) {
      showToast('error', error.message || 'Detay alınırken hata oluştu');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  useEffect(() => { load(); }, [load]);

  // #828 Distribütör tipi kullanıcı: yetkili distribütör otomatik seçili + DEĞİŞTİRİLEMEZ.
  // load() bittikten sonra çalışır; aksi halde kaydın kendi vendorId'si oto-seçimi ezerdi.
  useEffect(() => {
    if (!scopeLoaded || loading || !lockedVendorId) return;
    setForm((c) => (String(c.vendorId ?? '') === lockedVendorId ? c : { ...c, vendorId: lockedVendorId }));
  }, [scopeLoaded, loading, lockedVendorId]);

  // Klonla: /product-dynamic-groups/new'e kopya veriyle gelindiyse formu doldur (kayıt OLUŞTURMA — kullanıcı Kaydet diyene kadar).
  useEffect(() => {
    if (isNew && location.state?.clone) {
      const c = location.state.clone;
      setForm({ id: '', textId: c.textId || '', description: c.description || '', vendorId: c.vendorId || '' });
      setCriteria((c.criteria || []).map((cr) => ({
        id: '', criteriaType: cr.criteriaType, groupId: cr.groupId ?? null,
        subGroupId: cr.subGroupId ?? null, productId: cr.productId ?? null, label: cr.label || '',
      })));
      setDeletedDetailIds([]);
      setPreviewRows(null);
      setPoolChecked(new Set());
      setPoolFilter('');
      setTried(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // ── #725 İki panelli havuz kurucu ──
  // Bir kriterin (tip+id) havuzda olup olmadığını kontrol et — kaynak listesinden filtreler.
  const isAlreadyInPool = useCallback((type, valueId) => criteria.some((c) => (
    c.criteriaType === type && (
      (type === TYPE_GROUP && c.groupId === valueId) ||
      (type === TYPE_SUBGROUP && c.subGroupId === valueId) ||
      (type === TYPE_CODE && c.productId === valueId)
    )
  )), [criteria]);

  // Kod tipinde ürünler sunucudan (debounce'lu) aranır; Grup/Ek Grup zaten yüklü listeden filtrelenir.
  useEffect(() => {
    if (sourceType !== TYPE_CODE) return undefined;
    const handle = setTimeout(async () => {
      setProductSearching(true);
      try {
        const term = sourceSearch.trim().toLocaleLowerCase('tr').replace(/'/g, "''");
        const filter = term ? `&$filter=${encodeURIComponent(`contains(tolower(TextId),'${term}') or contains(tolower(Name),'${term}')`)}` : '';
        const res = await fetch(`/api/products?$top=50&$orderby=TextId${filter}`, { headers: authHeaders() });
        const body = await res.json().catch(() => null);
        const rows = body?.value ?? body?.Value ?? [];
        setProductResults(rows.map((p) => ({ id: p.Id ?? p.id, textId: p.TextId ?? p.textId, name: p.Name ?? p.name })));
      } catch {
        setProductResults([]);
      } finally {
        setProductSearching(false);
      }
    }, 300);
    return () => clearTimeout(handle);
  }, [sourceType, sourceSearch]);

  // Sol paneldeki seçilebilir değerler: tipe göre, arama ile filtreli, havuzda olanlar hariç.
  const sourceItems = useMemo(() => {
    const q = sourceSearch.trim().toLocaleLowerCase('tr');
    const match = (code, desc) => !q || `${code} ${desc}`.toLocaleLowerCase('tr').includes(q);
    if (sourceType === TYPE_GROUP) {
      return groups.filter((g) => !isAlreadyInPool(TYPE_GROUP, g.id) && match(g.textId || '', g.description || ''))
        .map((g) => ({ id: g.id, code: g.textId || '', desc: g.description || '' }));
    }
    if (sourceType === TYPE_SUBGROUP) {
      return subGroups.filter((g) => !isAlreadyInPool(TYPE_SUBGROUP, g.id) && match(g.textId || '', g.description || ''))
        .map((g) => ({ id: g.id, code: g.textId || '', desc: g.description || '' }));
    }
    return productResults.filter((p) => !isAlreadyInPool(TYPE_CODE, p.id))
      .map((p) => ({ id: p.id, code: p.textId || '', desc: p.name || '' }));
  }, [sourceType, sourceSearch, groups, subGroups, productResults, isAlreadyInPool]);

  const changeSourceType = (type) => {
    setSourceType(type);
    setSourceSearch('');
    setCheckedSource(new Set());
    if (type === TYPE_CODE) setProductResults([]);
  };

  const toggleChecked = (valueId) => setCheckedSource((prev) => {
    const next = new Set(prev);
    if (next.has(valueId)) next.delete(valueId); else next.add(valueId);
    return next;
  });

  const makeCriterion = (type, item) => {
    const suffix = item.desc ? `${item.code} - ${item.desc}` : item.code;
    if (type === TYPE_GROUP) return { id: '', criteriaType: TYPE_GROUP, groupId: item.id, subGroupId: null, productId: null, label: `Grup: ${suffix}` };
    if (type === TYPE_SUBGROUP) return { id: '', criteriaType: TYPE_SUBGROUP, groupId: null, subGroupId: item.id, productId: null, label: `Ek Grup: ${suffix}` };
    return { id: '', criteriaType: TYPE_CODE, groupId: null, subGroupId: null, productId: item.id, label: `Kod: ${suffix}` };
  };

  const transferOne = (item) => {
    if (isAlreadyInPool(sourceType, item.id)) return;
    setCriteria((cur) => [...cur, makeCriterion(sourceType, item)]);
    setCheckedSource((prev) => { const n = new Set(prev); n.delete(item.id); return n; });
    setPreviewRows(null);
  };

  const transferChecked = () => {
    const toAdd = sourceItems.filter((it) => checkedSource.has(it.id));
    if (toAdd.length === 0) { showToast('error', 'Aktarılacak değer seçilmedi'); return; }
    setCriteria((cur) => [...cur, ...toAdd.map((it) => makeCriterion(sourceType, it))]);
    setCheckedSource(new Set());
    setPreviewRows(null);
  };

  const onDropToPool = (e) => {
    e.preventDefault();
    setDragOver(false);
    const it = dragItemRef.current;
    dragItemRef.current = null;
    if (it) transferOne(it);
  };

  const removeCriterion = (index) => {
    setCriteria((current) => {
      const target = current[index];
      if (target?.id) setDeletedDetailIds((ids) => (ids.includes(target.id) ? ids : [...ids, target.id]));
      return current.filter((_, i) => i !== index);
    });
    setPreviewRows(null);
  };

  // #742 Havuzdaki kriterleri benzersiz anahtarla eşle (çoklu seçim için); duplicate zaten engelli.
  const criterionKeyOf = (c) => `${c.criteriaType}-${c.groupId || ''}-${c.subGroupId || ''}-${c.productId || ''}`;
  const togglePoolChecked = (key) => setPoolChecked((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  // #742 Seçili kriterleri havuzdan toplu çıkar (server id'liler deletedDetailIds'e eklenir).
  const removeCheckedFromPool = () => {
    if (poolChecked.size === 0) return;
    const removedIds = criteria.filter((c) => poolChecked.has(criterionKeyOf(c)) && c.id).map((c) => c.id);
    if (removedIds.length) setDeletedDetailIds((ids) => [...new Set([...ids, ...removedIds])]);
    setCriteria((current) => current.filter((c) => !poolChecked.has(criterionKeyOf(c))));
    setPoolChecked(new Set());
    setPreviewRows(null);
  };

  const buildDetailPayload = () => criteria.map((c) => ({
    id: c.id || '00000000-0000-0000-0000-000000000000',
    criteriaType: c.criteriaType,
    groupId: c.groupId,
    subGroupId: c.subGroupId,
    productId: c.productId,
  }));

  const previewPool = async () => {
    if (criteria.length === 0) { showToast('error', 'Önce kriter ekleyin'); return; }
    setPreviewLoading(true);
    try {
      const res = await fetch(`${API_BASE}/ResolveProducts`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ details: buildDetailPayload() }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Önizleme başarısız');
      const pool = body?.data ?? body?.Data ?? [];
      setPreviewRows(Array.isArray(pool) ? pool : []);
    } catch (error) {
      showToast('error', error.message || 'Önizleme sırasında hata oluştu');
    } finally {
      setPreviewLoading(false);
    }
  };

  // andNew: kaydet ama listeye/detaya gitme — formu sıfırlayıp boş yeni kayda hazırlan. Sadece yeni kayıt modunda anlamlı.
  const persist = async ({ andNew = false } = {}) => {
    setTried(true);
    if (!form.textId.trim()) { showToast('error', 'Kod boş olamaz'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/SaveWithDetails`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          // #828 Boş distribütör = tüm distribütörler için geçerli → backend'e null gider ('' değil).
          header: {
            id: form.id || '00000000-0000-0000-0000-000000000000',
            textId: form.textId,
            description: form.description,
            vendorId: form.vendorId || null,
          },
          details: buildDetailPayload(),
          deletedDetailIds,
        }),
      });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt kaydedilemedi');
      if (isNew) {
        if (andNew) {
          showToast('success', 'Özelleştirilmiş grup eklendi. Yeni kayda devam edebilirsiniz.');
          setForm(EMPTY_FORM);
          setCriteria([]);
          setDeletedDetailIds([]);
          setPreviewRows(null);
          setCheckedSource(new Set());
          setSourceSearch('');
          setPoolChecked(new Set());
          setPoolFilter('');
          setTried(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        showToast('success', 'Özelleştirilmiş grup başarıyla kaydedildi');
        setTimeout(() => navigate('/product-dynamic-groups'), 400);
        return;
      }
      showToast('success', 'Özelleştirilmiş grup başarıyla kaydedildi');
      setTimeout(() => navigate('/product-dynamic-groups'), 400);
    } catch (error) {
      showToast('error', error.message || 'İşlem sırasında hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleSave = () => persist();

  // Klonla: mevcut kaydın tüm form + kriter bilgilerini taşıyarak yeni-kayıt ekranını aç. Hiçbir şey kaydedilmez.
  const handleClone = () => {
    navigate('/product-dynamic-groups/new', {
      state: {
        clone: {
          textId: form.textId,
          description: form.description,
          vendorId: form.vendorId,
          criteria: criteria.map((c) => ({
            criteriaType: c.criteriaType, groupId: c.groupId, subGroupId: c.subGroupId, productId: c.productId, label: c.label,
          })),
        },
      },
    });
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/Delete`, { method: 'PUT', headers: authHeaders(), body: JSON.stringify({ id: form.id }) });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error(body?.message || body?.Message || 'Kayıt silinemedi');
      showToast('success', 'Özelleştirilmiş grup başarıyla silindi');
      setTimeout(() => navigate('/product-dynamic-groups'), 400);
    } catch (error) {
      showToast('error', error.message || 'Silme işlemi sırasında hata oluştu');
      setSaving(false);
      setDeleteConfirm(false);
    }
  };

  const tabs = [
    { key: 'general', label: 'Genel', icon: 'box' },
    { key: 'preview', label: 'Havuz Önizleme', icon: 'list' },
  ];

  const actions = (
    <>
      {!isNew && canDelete && <button className="dps-btn danger" onClick={() => setDeleteConfirm(true)} disabled={saving}><Icon name="x" size={16} /> Sil</button>}
      <button className="dps-btn ghost" onClick={() => navigate('/product-dynamic-groups')} disabled={saving}>İptal</button>
      {canSave && (isNew ? (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Kaydet ve Yeni',
            desc: 'Kaydet, listeye dönme, yeni kayıt aç',
            icon: 'plus',
            onClick: () => persist({ andNew: true }),
          }]}
        />
      ) : (
        <SplitButton
          label={saving ? 'Kaydediliyor…' : 'Kaydet'}
          icon="check"
          onClick={handleSave}
          disabled={saving}
          menu={[{
            label: 'Klonla',
            desc: 'Bu kaydı kopyalayıp yeni grup aç',
            icon: 'copy',
            onClick: handleClone,
          }]}
        />
      ))}
    </>
  );

  return (
    <DetailShell
      crumb={[{ label: 'Ürün Özelleştirilmiş Grup', to: '/product-dynamic-groups' }, { label: isNew ? 'Yeni Özelleştirilmiş Grup' : 'Özelleştirilmiş Grup Detayı', current: true }]}
      tabs={tabs}
      activeTab={activeTab}
      onTab={setActiveTab}
      actions={actions}
      audit={isNew ? null : audit}
      history={isNew ? null : { entityName: 'ProductDynamicGroup', entityId: id }}
      loading={loading}
    >
      {activeTab === 'general' && (
        <div className="dps-form">
          <Section icon="box" title="Grup Bilgileri" subtitle="Kod, açıklama ve distribütör kapsamı">
            <Grid cols={2}>
              <FieldText form={form} set={set} tried={tried} k="textId" label="Kod" req mono />
              <FieldText form={form} set={set} k="description" label="Açıklama" />
              {/* #828 Distribütör — zorunlu değil. Boş bırakılırsa tanım tüm distribütörler için
                  geçerlidir; seçilirse yalnız seçili distribütör için. Distribütör tipi kullanıcıda
                  yetkili distribütör otomatik seçili ve değiştirilemez. */}
              <FieldSelect
                form={form} set={set} k="vendorId" label="Distribütör"
                options={isDistributorUser && lockedVendorId ? lockedOptions : vendorOptions}
                disabled={Boolean(isDistributorUser && lockedVendorId)}
                hint={isDistributorUser && lockedVendorId
                  ? 'Distribütör tipi kullanıcı — yetkili distribütörünüz otomatik seçildi.'
                  : 'Boş bırakılırsa tüm distribütörler için geçerli olur.'}
              />
            </Grid>
          </Section>

          <Section
            icon="layers"
            title="Ürün Havuzu Kuralları"
            subtitle="Soldan kriter tipini seçip değerleri işaretleyin, “Havuza Aktar” ile (ya da sürükleyip bırakarak) sağdaki havuza taşıyın."
          >
            <div style={PS.wrap}>
              {/* Sol panel — kaynak seçim */}
              <div style={PS.col}>
                <div style={PS.tabs}>
                  {TYPE_OPTIONS.map((t) => (
                    <button key={t.value} type="button" style={PS.tab(sourceType === t.value)} onClick={() => changeSourceType(t.value)}>{t.label}</button>
                  ))}
                </div>
                <input
                  style={PS.search}
                  placeholder={sourceType === TYPE_CODE ? 'Ürün kodu / adı ara…' : 'Filtrele…'}
                  value={sourceSearch}
                  onChange={(e) => setSourceSearch(e.target.value)}
                />
                <div style={PS.list}>
                  {sourceType === TYPE_CODE && productSearching ? (
                    <div style={PS.hint}>Aranıyor…</div>
                  ) : sourceItems.length === 0 ? (
                    <div style={PS.hint}>
                      {sourceType === TYPE_CODE && !sourceSearch.trim() ? 'Ürün aramak için yazın.' : 'Sonuç yok.'}
                    </div>
                  ) : sourceItems.map((it) => (
                    <label
                      key={it.id}
                      style={PS.item}
                      draggable
                      onDragStart={() => { dragItemRef.current = it; }}
                    >
                      <input type="checkbox" checked={checkedSource.has(it.id)} onChange={() => toggleChecked(it.id)} />
                      <span style={PS.code}>{it.code}</span>
                      {it.desc && <span style={PS.desc}>{it.desc}</span>}
                      <button type="button" style={PS.add1} title="Havuza ekle" onClick={(e) => { e.preventDefault(); transferOne(it); }}>＋</button>
                    </label>
                  ))}
                </div>
                <button type="button" className="dps-btn primary" style={PS.transfer} onClick={transferChecked} disabled={checkedSource.size === 0}>
                  Havuza Aktar ▶{checkedSource.size > 0 ? ` (${checkedSource.size})` : ''}
                </button>
              </div>

              {/* Sağ panel — havuz (drop hedefi) */}
              <div
                style={PS.target(dragOver)}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDropToPool}
              >
                <div style={PS.poolHead}>Havuz · {criteria.length} kriter</div>
                {criteria.length === 0 ? (
                  <div style={PS.empty}>Henüz kriter eklenmedi.<br />Soldan değer seçip “Havuza Aktar” deyin ya da sürükleyip buraya bırakın.</div>
                ) : (
                  <>
                    {/* #742 Havuz içi: filtre + çoklu seçim + toplu "Havuzdan Çıkart". */}
                    <input
                      style={{ ...PS.search, marginBottom: 8 }}
                      placeholder="Havuzda filtrele…"
                      value={poolFilter}
                      onChange={(e) => setPoolFilter(e.target.value)}
                    />
                    {poolChecked.size > 0 && (
                      <button type="button" className="dps-btn danger" style={{ width: '100%', marginBottom: 8 }} onClick={removeCheckedFromPool}>
                        Havuzdan Çıkart ({poolChecked.size})
                      </button>
                    )}
                    <ul style={PS.chips}>
                      {(() => {
                        const q = poolFilter.trim().toLocaleLowerCase('tr');
                        const visible = criteria
                          .map((c, index) => ({ c, index }))
                          .filter(({ c }) => !q || String(c.label || '').toLocaleLowerCase('tr').includes(q));
                        if (visible.length === 0) return <li style={{ ...PS.empty, padding: 12 }}>Filtreye uyan kriter yok.</li>;
                        return visible.map(({ c, index }) => {
                          const key = criterionKeyOf(c);
                          return (
                            <li key={c.id || `${key}-${index}`} style={PS.chip}>
                              <input
                                type="checkbox"
                                checked={poolChecked.has(key)}
                                onChange={() => togglePoolChecked(key)}
                                style={{ flexShrink: 0 }}
                              />
                              <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.label || '(kriter)'}</span>
                              <button type="button" style={PS.chipX} title="Çıkar" onClick={() => removeCriterion(index)}>✕</button>
                            </li>
                          );
                        });
                      })()}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </Section>
        </div>
      )}

      {activeTab === 'preview' && (
        <div className="dps-form">
          <Section
            icon="list"
            title="Havuz Önizleme"
            subtitle="Tanımlı kriterlere uyan ürünleri görüntüleyin"
            right={(
              <button className="dps-blk-add" onClick={previewPool} disabled={previewLoading}>
                <Icon name="list" size={15} /> {previewLoading ? 'Hesaplanıyor…' : 'Havuzu Önizle'}
              </button>
            )}
          >
            {!Array.isArray(previewRows) ? (
              <div className="dps-empty"><Icon name="list" size={34} /><p>Henüz önizleme yapılmadı</p><span>Sağ üstten “Havuzu Önizle” ile kriterlere uyan ürünleri listeleyin.</span></div>
            ) : previewRows.length === 0 ? (
              <div className="dps-empty"><Icon name="list" size={34} /><p>Kritere uyan ürün bulunamadı</p><span>Genel sekmesinden kriterleri gözden geçirin.</span></div>
            ) : (
              <>
                <div style={{ marginBottom: 8, fontSize: 13, fontWeight: 600, color: 'var(--text-secondary, #374151)' }}>{previewRows.length} ürün</div>
                <table className="dps-table">
                  <thead>
                    <tr><th>Kod</th><th>Ad</th></tr>
                  </thead>
                  <tbody>
                    {previewRows.map((p) => (
                      <tr key={p.id || p.Id}>
                        <td><strong>{p.textId ?? p.TextId}</strong></td>
                        <td>{p.name ?? p.Name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </Section>
        </div>
      )}

      {/* Silme onayı */}
      {deleteConfirm && (
        <SubModal
          title="Silme Onayı"
          maxWidth={440}
          onClose={() => setDeleteConfirm(false)}
          footer={(
            <>
              <button className="dps-btn ghost" onClick={() => setDeleteConfirm(false)} disabled={saving}>İptal</button>
              <button className="dps-btn danger" onClick={handleDelete} disabled={saving}>{saving ? 'Siliniyor…' : 'Evet, Sil'}</button>
            </>
          )}
        >
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            <strong>“{form.textId}”</strong> kodlu özelleştirilmiş grubu silmek istediğinize emin misiniz? Bağlı tüm kriterler de silinecek.
          </p>
        </SubModal>
      )}

      <Toast toast={toast} />
    </DetailShell>
  );
}

export default ProductDynamicGroupDetailPage;
